import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Country } from '@app-shared/models/country.model';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import {
  entitiesPropsFactory,
  getAllEntities,
  selectAllEntities,
  selectAllEntitiesApply,
  setEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { Observable, shareReplay } from 'rxjs';
export interface CountriesFilterProps {
  loading: boolean;
  error: HttpErrorResponse | null;
}

const initialProps: CountriesFilterProps = {
  loading: false,
  error: null,
};

const STORE_NAME = 'sandoz-v2-countries-filter';

@Injectable({ providedIn: 'root' })
export class CountriesFilterRepository {
  private readonly store;
  private readonly selectedCountriesEntitiesRef;

  public readonly loading$: Observable<boolean>;
  public readonly error$: Observable<HttpErrorResponse | null>;
  public readonly countries$: Observable<Country[]>;
  public readonly selectedCountries$: Observable<Country[]>;
  public readonly selectedCountriesIds$: Observable<number[]>;

  constructor() {
    const { selectedCountriesEntitiesRef, withSelectedCountriesEntities } =
      entitiesPropsFactory('selectedCountries');

    this.selectedCountriesEntitiesRef = selectedCountriesEntitiesRef;

    this.store = createStore(
      { name: STORE_NAME },
      withProps<CountriesFilterProps>(initialProps),
      withEntities<Country>(),
      withSelectedCountriesEntities<Country>(),
    );

    this.loading$ = this.store.pipe(
      select(state => state.loading),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.error$ = this.store.pipe(
      select(state => state.error),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.countries$ = this.store.pipe(
      selectAllEntities(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.selectedCountries$ = this.store.pipe(
      selectAllEntities({ ref: this.selectedCountriesEntitiesRef }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.selectedCountriesIds$ = this.store.pipe(
      selectAllEntitiesApply({
        mapEntity: e => e.id,
        ref: this.selectedCountriesEntitiesRef,
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  resetStore(): void {
    this.store.reset();
  }

  getStoreProp<K extends keyof CountriesFilterProps>(
    key: K,
  ): CountriesFilterProps[K] {
    return this.store.getValue()[key];
  }

  // Entities methods
  getSelectedCountries(): Country[] {
    return this.store.query(
      getAllEntities({ ref: this.selectedCountriesEntitiesRef }),
    );
  }

  getSelectedCountriesIDs(): number[] {
    return this.store
      .query(getAllEntities({ ref: this.selectedCountriesEntitiesRef }))
      .map(country => country.id);
  }

  setCountries(countries: Country[]): void {
    this.store.update(setEntities(countries));
  }

  setSelectedCountries(countries: Country[]): void {
    this.store.update(
      setEntities(countries, { ref: this.selectedCountriesEntitiesRef }),
    );
  }

  // Props methods
  setLoading(loading: CountriesFilterProps['loading']): void {
    this.store.update(setProp('loading', loading));
  }

  setError(error: CountriesFilterProps['error']): void {
    this.store.update(setProp('error', error));
  }
}
